import React, { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Lock, Sparkles } from 'lucide-react';
import { VSL_VIDEOS, BRAND } from '../lib/brand';
import { logger } from '../lib/logger';
import VslForm1Popup from './VslForm1Popup';

// ═════════════════════════════════════════════════════════════════════
// VslGate — Plain iframe YouTube embed + Form 1 lead-capture popup
//
// PURPOSE: serve come hero della Pagina 1 (modalità persuasive). Mostra
// SOLO il video YouTube (controlli classici, autoplay muted) e gestisce
// il popup Form 1 obbligatorio. Una volta che Form 1 è compilato, il
// genitore (PublicLanding) flippa hasForm1=true e rivela la Pagina 2
// con tutta la landing.
//
// DESIGN CHOICE: usiamo un <iframe> plain invece della YouTube IFrame
// API per massima compatibilità. È letteralmente lo stesso codice del
// pulsante "Incorpora" di YouTube → autoplay funziona sempre.
// Trade-off: niente tracking Meta Pixel milestones (25/50/75/100) e
// niente backend track-signal per `video_progress`. Nel nuovo flusso a
// 2 pagine questi non sono più necessari perché il gate è Form 1.
//
// Form 1 popup triggers:
//   • 30 s dal mount del componente (timer indipendente dal video)
//   • Primo scroll oltre 40% del viewport (embedded mode)
//   • Custom event `gp:force-form1` (CTA cliccate prima del form)
// ═════════════════════════════════════════════════════════════════════

const STORAGE_PHASE1 = 'gp_vsl_phase1';
// ★ 2026-08-14 (Fase 52) — Ridotto da 30s a 8s: se il video YouTube ha embed
// disabilitato (issue attuale), l'utente vede "Video unavailable" e potrebbe
// abbandonare. Con 8s catturiamo il lead prima del bounce.
const FORM1_POPUP_DELAY_MS = 8000;
const VIDEO_ID = VSL_VIDEOS.initial.youtubeId;

export default function VslGate({ embedded = false }) {
  const { t } = useTranslation();
  const [open] = useState(true); // gate is always "open" in the new flow
  const [showForm1Popup, setShowForm1Popup] = useState(false);
  // ★ 2026-08-14 (Fase 52) — Poster overlay HCF branded sopra l'iframe.
  // Nasconde "Video unavailable" se l'embed è disabilitato dal proprietario.
  // Rimane visibile finché non viene compilato il Form 1 (o cliccato via CTA).
  const [posterDismissed, setPosterDismissed] = useState(false);

  // Has the visitor already submitted Form 1 in a previous session?
  // Read once on mount; the parent (PublicLanding) handles the live update.
  const form1AlreadyDoneRef = useRef(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_PHASE1);
      if (raw) {
        form1AlreadyDoneRef.current = true;
        setPosterDismissed(true);
      }
    } catch (err) {
      logger.debug('VslGate: localStorage read failed', err);
    }
  }, []);

  // ★ HARD-FAIL-SAFE: timer for the Form 1 popup that starts AT MOUNT.
  // Independent from whether the YouTube iframe ever loads or plays.
  useEffect(() => {
    if (form1AlreadyDoneRef.current) return undefined;
    const tid = setTimeout(() => {
      let stillNeeded = true;
      try { stillNeeded = !localStorage.getItem(STORAGE_PHASE1); } catch (err) { logger.debug('VslGate: form1 re-check failed', err); }
      if (stillNeeded) setShowForm1Popup(true);
    }, FORM1_POPUP_DELAY_MS);
    return () => clearTimeout(tid);
  }, []);

  // ★ External CTAs (Continua a leggere, Scopri tutta la proposta, etc.)
  // dispatch `gp:force-form1` to open the popup before the visitor can
  // bypass the lead-capture. Listener stays alive for the whole mount.
  useEffect(() => {
    const handler = () => {
      if (form1AlreadyDoneRef.current) return;
      let stillNeeded = true;
      try { stillNeeded = !localStorage.getItem(STORAGE_PHASE1); } catch (err) { logger.debug('VslGate: gp:force-form1 re-check failed', err); }
      if (stillNeeded) setShowForm1Popup(true);
    };
    window.addEventListener('gp:force-form1', handler);
    return () => window.removeEventListener('gp:force-form1', handler);
  }, []);

  // ★ Body scroll lock while the Form 1 popup is open — works in BOTH
  // embedded (persuasive) and fullscreen (gate) modes. Without this, the
  // visitor could scroll past the popup in persuasive mode.
  useEffect(() => {
    if (!showForm1Popup) return undefined;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [showForm1Popup]);

  // ★ Scroll-trap for Form 1 (embedded mode only): if the visitor scrolls
  // past 40 % of the first viewport without compiling Form 1 yet, open
  // the popup. Edge case for visitors who skip the 30 s wait or never
  // start the video.
  useEffect(() => {
    if (!embedded) return undefined;
    let triggered = false;
    const onScroll = () => {
      if (triggered) return;
      try {
        if (localStorage.getItem(STORAGE_PHASE1)) { triggered = true; return; }
      } catch (err) { logger.debug('VslGate: scroll-trap localStorage read failed', err); }
      const minScroll = Math.max(120, window.innerHeight * 0.4);
      if (window.scrollY < minScroll) return;
      triggered = true;
      setShowForm1Popup(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [embedded]);

  // In legacy fullscreen mode the gate disappears once Form 1 is submitted.
  // (Currently unused in production — LANDING_MODE='persuasive'.)
  if (!embedded && !open) return null;

  // ★ Standard YouTube embed URL — identical to the one generated by the
  // "Embed" button on youtube.com. Autoplay muted is the only way modern
  // browsers (Chrome 90+, Safari 14+) accept autoplay; the user clicks
  // the volume button to unmute via native YouTube controls.
  const embedUrl = `https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&mute=1&playsinline=1&rel=0`;

  return (
    <div
      className={embedded
        ? 'relative w-full bg-gradient-to-b from-black via-[#0a1a14] to-[#0f1f17] px-3 sm:px-6 md:px-10 py-6 md:py-10 overflow-hidden gp-noise'
        : 'fixed inset-0 z-[10000] bg-black/95 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 md:p-10 overflow-y-auto'}
      data-testid="vsl-gate"
      role={embedded ? undefined : 'dialog'}
      aria-modal={embedded ? undefined : 'true'}
    >
      {/* Branded corners (only in fullscreen mode — in embedded the page
          has its own header so duplicates would clutter the hero). */}
      {!embedded && (
        <>
          <div className="absolute top-4 left-4 flex items-center gap-2 text-white/80 text-xs tracking-[0.25em] uppercase z-10">
            <img
              src={BRAND.logo}
              alt="HCF Property"
              className="h-9 w-auto rounded-md"
            />
            <span className="hidden sm:inline">{t('vslGate.exclusiveView', 'Visione esclusiva')}</span>
          </div>
          <div className="absolute top-4 right-4 flex items-center gap-1.5 text-white/60 text-[11px] z-10">
            <Lock className="w-3 h-3" />
            <span className="hidden sm:inline">{t('vslGate.restrictedContent', 'Contenuto riservato')}</span>
          </div>
        </>
      )}

      <div className="relative w-full max-w-5xl mx-auto my-auto">
        <div className="text-center mb-4 md:mb-6 text-white">
          <div className="inline-flex items-center gap-2 text-[10px] md:text-xs tracking-[0.3em] uppercase opacity-80">
            <Sparkles className="w-3 h-3 text-[var(--gp-gold-soft)]" />
            {t('vslGate.beforeYouEnter', 'Prima di entrare')}
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-[2.4rem] leading-[1.05] mt-2">
            {t('vslGate.titlePrefix', 'La presentazione ufficiale di')} <span className="italic text-[var(--gp-gold-soft)]">{t('vslGate.titleBrand', 'HCF Property')}</span>.
          </h2>
        </div>

        {/* Video stage (16:9) — plain YouTube iframe embed with native
            controls. Autoplay muted (user clicks volume to unmute).
            ★ 2026-08-14 (Fase 52) — Poster overlay HCF-branded che copre
            l'iframe fino al click. Fa 2 cose:
             1. Nasconde eventuale "Video unavailable" se l'embed è disabilitato
             2. Trasforma il gate in un'esperienza premium coerente col brand
            Al click apre il Form 1 (lead capture) — dopo la submit il poster
            si nasconde e (se il video funziona) parte l'embed. */}
        <div
          className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-black"
          style={{ aspectRatio: '16 / 9' }}
        >
          <iframe
            title={t('vslGate.iframeTitle', 'HCF Property — La presentazione ufficiale')}
            src={embedUrl}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            loading="eager"
            data-testid="vsl-gate-iframe"
          />

          {/* Poster overlay — visibile fino a compilazione Form 1 */}
          {!posterDismissed && (
            <button
              type="button"
              onClick={() => {
                if (form1AlreadyDoneRef.current) {
                  setPosterDismissed(true);
                  return;
                }
                setShowForm1Popup(true);
              }}
              data-testid="vsl-gate-poster"
              className="absolute inset-0 w-full h-full group cursor-pointer overflow-hidden text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--gp-gold)]/50"
              aria-label={t('vslGate.posterAria', 'Sblocca la presentazione ufficiale HCF Property')}
            >
              {/* Background layered gradient + branded pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#0a1f16] via-[#0f2a1e] to-[#153b2a]" />
              <div className="absolute inset-0 opacity-40 gp-noise" />
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: 'radial-gradient(ellipse at 30% 20%, rgba(180,138,45,0.35), transparent 55%), radial-gradient(ellipse at 70% 80%, rgba(180,138,45,0.25), transparent 55%)',
                }}
              />
              {/* Elegant gold border frame */}
              <div className="absolute inset-4 border border-[var(--gp-gold)]/25 rounded-lg pointer-events-none" />
              {/* Content */}
              <div className="relative z-10 flex flex-col items-center justify-center h-full w-full text-center px-6 md:px-10">
                <div className="text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-[var(--gp-gold)]/90 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="inline-block w-8 h-px bg-[var(--gp-gold)]/50" />
                  {t('vslGate.posterEyebrow', 'Presentazione riservata')}
                  <span className="inline-block w-8 h-px bg-[var(--gp-gold)]/50" />
                </div>
                <div className="mb-5 md:mb-7">
                  <img
                    src={BRAND.logo}
                    alt="HCF Property"
                    className="h-14 md:h-16 lg:h-20 w-auto mx-auto"
                  />
                </div>
                <h3 className="font-serif text-white text-2xl md:text-3xl lg:text-4xl leading-tight max-w-2xl">
                  {t('vslGate.posterTitleLine1', "Il futuro dell'")}
                  <span className="italic text-[var(--gp-gold-soft)]">{t('vslGate.posterTitleAccent', 'investimento immobiliare')}</span>
                  {' '}{t('vslGate.posterTitleLine2', 'internazionale')}
                </h3>
                <p className="text-white/70 text-xs md:text-sm mt-3 md:mt-4 max-w-md">
                  {t('vslGate.posterSubtitle', 'Scopri come HCF Property costruisce ricchezza attraverso resort in Albania, Indonesia, Thailandia e Norvegia.')}
                </p>
                {/* Play button */}
                <div className="mt-6 md:mt-8 inline-flex items-center gap-3 rounded-full bg-[var(--gp-gold)] text-[hsl(var(--primary))] pl-3 pr-6 py-2.5 md:py-3 font-medium shadow-[0_15px_50px_-15px_rgba(180,138,45,0.7)] group-hover:shadow-[0_20px_60px_-15px_rgba(180,138,45,1)] group-hover:scale-[1.03] transition-all">
                  <span className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/95 grid place-items-center">
                    <svg className="w-4 h-4 md:w-5 md:h-5 fill-[var(--gp-gold)]" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                  <span className="text-sm md:text-base">{t('vslGate.posterCta', "Guarda l'anteprima")}</span>
                </div>
                <div className="mt-4 flex items-center gap-4 text-[10px] md:text-[11px] text-white/50">
                  <span className="inline-flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    {t('vslGate.premiumContent', 'Contenuto premium')}
                  </span>
                  <span>·</span>
                  <span>{t('vslGate.duration', 'Durata: 20 minuti')}</span>
                </div>
              </div>
            </button>
          )}
        </div>

        {/* ★ 2026-08-14 (Fase 52) — CTA immediato sotto il video.
            Se il video non parte (embed disabilitato o slow network) l'utente
            ha comunque un bottone dorato per accedere subito al form Phase 1
            e non abbandonare la landing. */}
        <div className="mt-4 md:mt-5 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (form1AlreadyDoneRef.current) return;
              setShowForm1Popup(true);
            }}
            data-testid="vsl-gate-unlock-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-[var(--gp-gold)] text-[hsl(var(--primary))] px-6 md:px-8 py-3 md:py-3.5 text-sm md:text-base font-medium shadow-[0_10px_40px_-10px_rgba(180,138,45,0.6)] hover:shadow-[0_15px_50px_-10px_rgba(180,138,45,0.9)] hover:scale-[1.02] transition-all"
          >
            <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
            {t('vslGate.unlockCta', 'Sblocca la presentazione completa')}
          </button>
          <p className="text-[11px] md:text-xs text-white/60 text-center max-w-md">
            {t('vslGate.fallbackHint', 'Se il video non si carica, clicca qui per accedere subito ai contenuti riservati.')}
          </p>
        </div>
      </div>

      {/* ★ Form-1 popup — appears 30 s after mount (or sooner via gp:force-form1
          event or scroll trigger). Blocking modal: visitor MUST submit name +
          email + phone to dismiss. On success, the parent flips `hasForm1`
          and the persuasive landing reveals the full Page 2 content. */}
      <VslForm1Popup
        open={showForm1Popup}
        onSubmitted={() => {
          form1AlreadyDoneRef.current = true;
          setShowForm1Popup(false);
          setPosterDismissed(true);
        }}
      />
    </div>
  );
}
